import { useCallback, useEffect, useRef, useState } from 'react';
import { on, removeListener, run } from '../helper/electron';
import { useDispatch } from '../store/hooks';
import { invalidateLoadedMatches } from '../store/slices/track-matcher.slice';
import isElectron from '../utils/isElectron';
import {
  describeVdjCacheIndex,
  isVdjCacheEnabled,
  persistVdjCacheEnabled,
  VdjCacheIndexStatus,
  vdjCacheMatchesAreLive,
} from '../utils/vdjCacheIndexStatus';

const WARMUP = 'virtualdj/cache-index/warmup';
const STATUS = 'virtualdj/cache-index/status';
const PROGRESS = 'virtualdj/cache-index/progress';

export function useVdjCacheIndex() {
  const dispatch = useDispatch();
  const [enabled, setEnabledState] = useState(() => isVdjCacheEnabled());
  const [status, setStatus] = useState<VdjCacheIndexStatus | null>(null);
  const previousState = useRef<VdjCacheIndexStatus['state'] | null>(null);

  const warmup = useCallback((force = false) => {
    if (!isElectron() || !isVdjCacheEnabled()) return;
    run(WARMUP, force ? { force: true } : undefined);
  }, []);

  useEffect(() => {
    if (!isElectron()) return;

    const listener = (payload: VdjCacheIndexStatus) => setStatus(payload);
    on(PROGRESS, listener);
    run(STATUS);

    return () => {
      removeListener(PROGRESS, listener);
    };
  }, []);

  useEffect(() => {
    if (!isElectron() || !enabled) return;
    if (
      status?.state === 'indexing' ||
      status?.state === 'ready' ||
      status?.state === 'error'
    ) {
      return;
    }
    warmup(false);
  }, [enabled, status?.state, warmup]);

  useEffect(() => {
    const next = status?.state ?? null;
    if (previousState.current === 'indexing' && next === 'ready' && enabled) {
      dispatch(invalidateLoadedMatches());
    }
    previousState.current = next;
  }, [status?.state, enabled, dispatch]);

  const setEnabled = useCallback(
    (next: boolean) => {
      persistVdjCacheEnabled(next);
      setEnabledState(next);
      if (next) {
        warmup(false);
      } else {
        dispatch(invalidateLoadedMatches());
      }
    },
    [dispatch, warmup],
  );

  return {
    enabled,
    setEnabled,
    status,
    ready: vdjCacheMatchesAreLive(status),
    note: describeVdjCacheIndex(enabled, status),
    warmup,
  };
}
